/**
 * The UI store.
 *
 * A 60-line store built on `useSyncExternalStore` rather than a state-management
 * dependency. The game core already owns all the interesting state; React only
 * needs to know *that* it changed, so a library would be weight without benefit.
 */

import { useSyncExternalStore } from 'react';
import type { GameSession } from '@core/engine';
import {
  acknowledge,
  choose,
  createSession,
  endLife,
  moveTo,
  takeAction,
} from '@core/engine';
import { createNewLife } from '@core/newgame';
import type { NewLifeOptions } from '@core/newgame';
import { AUTOSAVE_SLOT, loadSlot, saveSlot, deleteSlot, listSlots } from '@core/persistence';
import type { LoadResult, SaveSummary } from '@core/persistence';
import type { PlayerState } from '@core/types';

export type Screen = 'title' | 'setup' | 'play' | 'ending';

export type PanelId =
  | null
  | 'character'
  | 'career'
  | 'finance'
  | 'relationships'
  | 'goals'
  | 'timeline'
  | 'settings'
  | 'saves';

export interface Settings {
  reducedMotion: boolean;
  highContrast: boolean;
  autosave: boolean;
  showNumbers: boolean;
}

export interface AppState {
  screen: Screen;
  session: GameSession | null;
  panel: PanelId;
  /** Non-blocking message shown as a toast. */
  notice: { id: number; text: string; tone: 'info' | 'warn' } | null;
  saves: { slot: string; summary: SaveSummary; savedAt: number }[];
  settings: Settings;
  busy: boolean;
}

const SETTINGS_KEY = 'alife.settings';

function readSettings(): Settings {
  const fallback: Settings = { reducedMotion: false, highContrast: false, autosave: true, showNumbers: true };
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return fallback;
    return { ...fallback, ...(JSON.parse(raw) as Partial<Settings>) };
  } catch {
    return fallback;
  }
}

function writeSettings(settings: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Storage is unavailable (private mode); settings simply do not persist.
  }
}

function applySettings(settings: Settings): void {
  const root = document.documentElement;
  root.dataset['reducedMotion'] = String(settings.reducedMotion);
  root.dataset['contrast'] = settings.highContrast ? 'high' : 'normal';
}

let state: AppState = {
  screen: 'title',
  session: null,
  panel: null,
  notice: null,
  saves: [],
  settings: readSettings(),
  busy: false,
};

const listeners = new Set<() => void>();

function set(patch: Partial<AppState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const snapshot = (): AppState => state;

export function useApp(): AppState {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Select a slice; re-renders only when the slice changes by identity. */
export function useSession(): GameSession | null {
  return useSyncExternalStore(
    subscribe,
    () => state.session,
    () => state.session,
  );
}

/* ------------------------------------------------------------------ *
 * Notices
 * ------------------------------------------------------------------ */

let noticeSeq = 0;
let noticeTimer: number | undefined;

export function notify(text: string, tone: 'info' | 'warn' = 'info'): void {
  noticeSeq += 1;
  set({ notice: { id: noticeSeq, text, tone } });
  if (noticeTimer !== undefined) window.clearTimeout(noticeTimer);
  noticeTimer = window.setTimeout(() => set({ notice: null }), 4200);
}

/* ------------------------------------------------------------------ *
 * Session lifecycle
 * ------------------------------------------------------------------ */

/**
 * The engine mutates `PlayerState` in place — deliberately, because the balance
 * lab runs millions of ticks and cloning per tick would dominate its runtime.
 * React, however, compares by identity: a component memoised on `[state]` would
 * never recompute, which is exactly how net worth froze at its starting value
 * for an entire life.
 *
 * Re-wrapping the state in a fresh shallow copy on every committed step gives
 * React a new identity for one object allocation, and the engine keeps
 * mutating whatever it is handed next.
 */
function commit(next: GameSession): GameSession {
  return { ...next, state: { ...next.state } };
}

function withSession(raw: GameSession): void {
  const next = commit(raw);
  const screen: Screen = next.phase === 'ended' ? 'ending' : 'play';
  set({ session: next, screen });
  if (state.settings.autosave) void autosave(next.state);
}

async function autosave(playerState: PlayerState): Promise<void> {
  try {
    await saveSlot(AUTOSAVE_SLOT, playerState);
  } catch {
    notify('Could not autosave. Your progress is only in this tab.', 'warn');
  }
}

export const actions = {
  goTitle(): void {
    set({ screen: 'title', panel: null });
    void actions.refreshSaves();
  },

  goSetup(): void {
    set({ screen: 'setup', panel: null });
  },

  startNewLife(options: NewLifeOptions): void {
    const session = commit(createSession(createNewLife(options)));
    set({ session, screen: 'play', panel: null });
    if (state.settings.autosave) void autosave(session.state);
  },

  move(nodeId: string): void {
    if (!state.session) return;
    withSession(moveTo(state.session, nodeId));
  },

  choose(choiceId: string): void {
    if (!state.session) return;
    withSession(choose(state.session, choiceId));
  },

  acknowledge(): void {
    if (!state.session) return;
    withSession(acknowledge(state.session));
  },

  act(actionId: string): void {
    if (!state.session) return;
    const raw = takeAction(state.session, actionId);
    if (raw === state.session) return;
    const next = commit(raw);
    set({ session: next, panel: null });
    if (state.settings.autosave) void autosave(next.state);
  },

  concludeLife(): void {
    if (!state.session) return;
    withSession(endLife(state.session, 'chose to stop here'));
  },

  openPanel(panel: PanelId): void {
    set({ panel });
  },

  closePanel(): void {
    set({ panel: null });
  },

  updateSettings(patch: Partial<Settings>): void {
    const settings = { ...state.settings, ...patch };
    writeSettings(settings);
    applySettings(settings);
    set({ settings });
  },

  async refreshSaves(): Promise<void> {
    try {
      set({ saves: await listSlots() });
    } catch {
      set({ saves: [] });
    }
  },

  async saveTo(slot: string): Promise<void> {
    if (!state.session) return;
    set({ busy: true });
    try {
      await saveSlot(slot, state.session.state);
      notify(slot === AUTOSAVE_SLOT ? 'Autosaved.' : `Saved to ${slot}.`);
      await actions.refreshSaves();
    } catch {
      notify('Save failed. Browser storage may be unavailable.', 'warn');
    } finally {
      set({ busy: false });
    }
  },

  async loadFrom(slot: string): Promise<void> {
    set({ busy: true });
    try {
      const result: LoadResult = await loadSlot(slot);
      if (!result.ok) {
        const message =
          result.reason === 'empty'
            ? 'That slot is empty.'
            : result.reason === 'unsupported'
              ? (result.detail ?? 'That save is from a newer version.')
              : (result.detail ?? 'That save could not be read.');
        notify(message, 'warn');
        return;
      }
      if (result.migrated) notify('Save upgraded from an older version.');
      const session = commit(createSession(result.state));
      set({ session, screen: session.phase === 'ended' ? 'ending' : 'play', panel: null });
    } catch {
      notify('Load failed.', 'warn');
    } finally {
      set({ busy: false });
    }
  },

  async deleteSave(slot: string): Promise<void> {
    await deleteSlot(slot);
    await actions.refreshSaves();
    notify('Deleted.');
  },

  async continueLatest(): Promise<void> {
    await actions.loadFrom(AUTOSAVE_SLOT);
  },
};

applySettings(state.settings);
void actions.refreshSaves();
